import {useRespondToAliveUpdate} from '../../hooks/use-refetch-on-alive-update'
import {Button, Link as PrimerLink} from '@primer/react'
import {useState, type MouseEventHandler} from 'react'
import styles from './RefreshButton.module.css'
import {SyncIcon} from '@primer/octicons-react'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useIsDataRouterEnabled} from '@github-ui/react-core/future/use-is-data-router-enabled'
import {Link as RouterLink, useLocation} from '@github-ui/react-router'

export function RefreshButton({aliveChannel, pathName}: {aliveChannel: string; pathName: string}) {
  const [show, setShow] = useState(false)
  // only need to check if using data router since refresh button is only shown in New Files Changed
  // since we are already on the files/changes route, we don't need to check for the target route itself
  const isDataRouter = useIsDataRouterEnabled()
  const combinedPrChangesRoute = useFeatureFlag('combined_pr_changes_route')
  const refreshUrl = combinedPrChangesRoute ? `${pathName}/changes` : `${pathName}/files`

  const liveUpdateHandler = () => {
    setShow(true)
  }

  // we only need to display the refresh button when the base or head sha has changed (via git_updated)
  useRespondToAliveUpdate(aliveChannel, liveUpdateHandler, {git_updated: true})

  if (!show) {
    return null
  }

  if (isDataRouter) {
    return <DataRouterRefreshButton />
  }

  return <NavigatorRefreshButton refreshUrl={refreshUrl} />
}

function NavigatorRefreshButton({refreshUrl}: {refreshUrl: string}) {
  const onRefreshClick: MouseEventHandler<HTMLButtonElement> = event => {
    // we want to hard refresh the page manually to avoid soft navigation
    // intercepting the navigation and not refreshing the page content
    event.preventDefault()
    window.location.href = refreshUrl
  }

  return (
    <Button
      as={PrimerLink}
      variant="invisible"
      href={refreshUrl}
      className={styles.refresh}
      leadingVisual={SyncIcon}
      size="small"
      onClick={onRefreshClick}
    >
      Refresh
    </Button>
  )
}

function DataRouterRefreshButton() {
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(false)

  const onClick = async () => {
    setIsLoading(true)
  }

  return (
    <Button
      as={RouterLink}
      // link to the current path to trigger a data router navigation
      // eventually need to apply active params but for now this is sufficient
      to={location.pathname}
      variant="invisible"
      className={styles.refresh}
      leadingVisual={SyncIcon}
      size="small"
      onClick={onClick}
      loading={isLoading}
    >
      Refresh
    </Button>
  )
}

try{ RefreshButton.displayName ||= 'RefreshButton' } catch {}
try{ NavigatorRefreshButton.displayName ||= 'NavigatorRefreshButton' } catch {}
try{ DataRouterRefreshButton.displayName ||= 'DataRouterRefreshButton' } catch {}